<template>
    <div class="pin-code-input" :class="{ 'pin-code-input--disabled': disabled }">
        <input
            v-for="index in 6"
            :key="index - 1"
            :ref="'box' + (index - 1)"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="one-time-code"
            maxlength="1"
            class="pin-code-input__box"
            :class="{
                'pin-code-input__box--filled': boxes[index - 1] !== '',
                'pin-code-input__box--active': activeIndex === (index - 1)
            }"
            :value="boxes[index - 1]"
            :disabled="disabled"
            :aria-label="boxAriaLabel(index - 1)"
            @input="onBoxInput($event, index - 1)"
            @keydown="onBoxKeydown($event, index - 1)"
            @paste.prevent="onPaste($event, index - 1)"
            @focus="activeIndex = index - 1"
            @blur="onBoxBlur(index - 1)" />
    </div>
</template>


<script>
export default {
    props: {
        value: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        digitLabel: {
            type: String,
            default: 'Digit',
        },
    },

    data: function () {
        return {
            boxes: ['', '', '', '', '', ''],
            activeIndex: -1,
        };
    },

    watch: {
        value: {
            immediate: true,
            handler: function (nextValue) {
                this.syncBoxesFromValue(nextValue);
            },
        },

        disabled: function (isDisabled, wasDisabled) {
            if (wasDisabled === true && isDisabled === false) {
                this.$nextTick(() => {
                    this.focusBox(0);
                });
            }
        },
    },

    methods: {
        syncBoxesFromValue: function (nextValue) {
            const clean = (nextValue || '').replace(/\D/g, '').slice(0, 6);
            const nextBoxes = clean.split('');

            while (nextBoxes.length < 6) {
                nextBoxes.push('');
            }

            this.boxes = nextBoxes;
        },

        boxAriaLabel: function (index) {
            return this.digitLabel + ' ' + (index + 1);
        },

        emitValue: function () {
            const nextValue = this.boxes.join('').replace(/\D/g, '').slice(0, 6);

            this.$emit('input', nextValue);

            if (nextValue.length === 6) {
                this.$emit('complete', nextValue);
            }
        },

        focusBox: function (index) {
            if (index < 0 || index > 5 || this.disabled) {
                return;
            }

            const ref = this.$refs['box' + index];
            const element = Array.isArray(ref) ? ref[0] : ref;

            if (element) {
                element.focus();
                element.select();
            }
        },

        onBoxInput: function (event, index) {
            const raw = (event.target.value || '').replace(/\D/g, '');
            const nextBoxes = this.boxes.slice();

            if (raw.length > 1) {
                this.applyDigits(raw, index);
                return;
            }

            nextBoxes[index] = raw.slice(-1);
            this.boxes = nextBoxes;
            this.emitValue();

            if (raw && index < 5) {
                this.$nextTick(() => {
                    this.focusBox(index + 1);
                });
            }
        },

        onBoxKeydown: function (event, index) {
            if (event.key === 'Backspace') {
                const nextBoxes = this.boxes.slice();

                if (nextBoxes[index]) {
                    nextBoxes[index] = '';
                    this.boxes = nextBoxes;
                    this.emitValue();
                    return;
                }

                if (index > 0) {
                    event.preventDefault();
                    nextBoxes[index - 1] = '';
                    this.boxes = nextBoxes;
                    this.emitValue();
                    this.$nextTick(() => {
                        this.focusBox(index - 1);
                    });
                }
                return;
            }

            if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                this.focusBox(index - 1);
                return;
            }

            if (event.key === 'ArrowRight' && index < 5) {
                event.preventDefault();
                this.focusBox(index + 1);
            }
        },

        onPaste: function (event, index) {
            const pasted = (event.clipboardData || window.clipboardData).getData('text') || '';
            this.applyDigits(pasted.replace(/\D/g, ''), index);
        },

        applyDigits: function (digits, startIndex) {
            const nextBoxes = this.boxes.slice();
            let cursor = startIndex;

            digits.split('').forEach((digit) => {
                if (cursor > 5) {
                    return;
                }
                nextBoxes[cursor] = digit;
                cursor += 1;
            });

            this.boxes = nextBoxes;
            this.emitValue();

            this.$nextTick(() => {
                this.focusBox(Math.min(cursor, 5));
            });
        },

        onBoxBlur: function (index) {
            if (this.activeIndex === index) {
                this.activeIndex = -1;
            }
        },

        focusFirst: function () {
            this.focusBox(0);
        },
    },
};
</script>


<style scoped>
.pin-code-input {
    display: flex;
    justify-content: center;
    gap: 8px;
}

.pin-code-input--disabled {
    opacity: 0.65;
}

.pin-code-input__box {
    width: 42px;
    height: 48px;
    padding: 0;
    border: 2px solid #d8d2c8;
    border-radius: 8px;
    background: #fff;
    color: #2d3e52;
    font-size: 22px;
    font-weight: 700;
    line-height: 48px;
    text-align: center;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.pin-code-input__box:focus,
.pin-code-input__box--active {
    outline: none;
    border-color: #d35400;
    box-shadow: 0 0 0 3px rgba(211, 84, 0, 0.15);
}

.pin-code-input__box--filled {
    border-color: #2d3e52;
    background: #faf9f6;
}

.pin-code-input__box:disabled {
    cursor: not-allowed;
    background: #f3f3f3;
}

@media only screen and (max-width: 420px) {
    .pin-code-input {
        gap: 6px;
    }

    .pin-code-input__box {
        width: 36px;
        height: 44px;
        font-size: 20px;
        line-height: 44px;
    }
}
</style>
